// «ضبط صفحه» of a ticket (60.11.579): the bar at the foot of the panel's other pages while the screen is recorded - its
// time, a pause, «پایان ضبط» (back to the ticket, where the video joins the reply) and «انصراف»; then, when it ended
// away from the ticket (its limit, the browser's own stop), the way back to the ticket or «حذف».
import {FC, useEffect, useId, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {clockLabel, SCREEN_MAX_SECONDS} from '../ticket-media'
import {
  dropScreenRecording,
  getScreenRecording,
  pauseScreenRecording,
  resumeScreenRecording,
  screenRecordingInReplyBox,
  screenRecordingSeconds,
  ScreenRecordingState,
  stopScreenRecording,
  subscribeScreenRecording,
} from './screen-recording'
import {focusCaptureWindow} from './capture-window'
import './screen-share.css'

export const ScreenRecordingHost: FC = () => {
  const [state, setState] = useState<ScreenRecordingState>(getScreenRecording)
  const [seconds, setSeconds] = useState(screenRecordingSeconds)
  const [confirmingDrop, setConfirmingDrop] = useState(false)
  const navigate = useNavigate()
  const titleId = useId()

  useEffect(() => subscribeScreenRecording(setState), [])

  const running = state.status === 'recording' || state.status === 'paused'
  useEffect(() => {
    setSeconds(screenRecordingSeconds())
    if (!running) return
    const timer = window.setInterval(() => setSeconds(screenRecordingSeconds()), 500)
    return () => window.clearInterval(timer)
  }, [running, state.status])

  useEffect(() => {
    if (state.status !== 'ready') setConfirmingDrop(false)
  }, [state.status])

  // A choice to make in the capture window is told on every page, its own ticket's too: the reply box has no word for it.
  const shown =
    state.status !== 'idle' && (state.status === 'choosing' || !screenRecordingInReplyBox(state))
  // The bar sits over the foot of the page: screen-share.css makes room below the page and lifts a reply box.
  useEffect(() => {
    document.body.classList.toggle('mp-screen-recording', shown)
    return () => document.body.classList.remove('mp-screen-recording')
  }, [shown])

  if (!shown) return null
  const ticketPath = `/apps/ticket-management/room/${encodeURIComponent(state.ticketNumber || '')}`
  const paused = state.status === 'paused'

  if (state.status === 'choosing')
    return (
      <div
        className='mp-screen-share-bar mp-screen-record-bar is-choosing'
        role='status'
        aria-labelledby={titleId}
        dir='rtl'
      >
        <span className='mp-screen-share-bar__dot' aria-hidden='true' />
        <span className='mp-screen-share-bar__icon' aria-hidden='true'>
          <i className='bi bi-camera-video' />
        </span>
        <span className='mp-screen-share-bar__words'>
          <strong id={titleId}>ضبط صفحه: صفحه را در پنجره‌ی «نمایش و ضبط صفحه» انتخاب کنید</strong>
          <small>
            ضبط در آن پنجره انجام می‌شود و با رفرش یا رفتن به صفحه‌های دیگر قطع نمی‌شود.
          </small>
        </span>
        <button
          type='button'
          className='btn btn-sm btn-primary'
          // The window is gone (closed by the customer): the request goes with it.
          onClick={() => focusCaptureWindow() || stopScreenRecording(true)}
        >
          <i className='bi bi-window-stack' aria-hidden='true' /> نمایش پنجره
        </button>
        <button
          type='button'
          className='btn btn-sm btn-light'
          onClick={() => stopScreenRecording(true)}
        >
          انصراف
        </button>
      </div>
    )

  if (state.status === 'ready')
    return (
      <div
        className='mp-screen-share-bar mp-screen-record-bar is-ready'
        role='status'
        aria-labelledby={titleId}
        dir='rtl'
      >
        <span className='mp-screen-share-bar__dot' aria-hidden='true' />
        <span className='mp-screen-share-bar__icon' aria-hidden='true'>
          <i className='bi bi-camera-video' />
        </span>
        <span className='mp-screen-share-bar__words'>
          <strong id={titleId}>
            {confirmingDrop ? 'این ضبط صفحه حذف شود؟' : 'ضبط صفحه آماده‌ی پیوست به تیکت است'}
          </strong>
          <small>
            {confirmingDrop
              ? 'پس از حذف، بازگرداندن آن ممکن نیست.'
              : `${clockLabel(
                  seconds
                )}؛ در تیکت به پاسخ شما پیوست می‌شود و پیش از ارسال می‌توانید آن را ببینید.`}
          </small>
        </span>
        {confirmingDrop ? (
          <>
            <button
              type='button'
              className='btn btn-sm btn-danger'
              onClick={() => dropScreenRecording()}
            >
              <i className='bi bi-trash' aria-hidden='true' /> حذف ضبط
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => setConfirmingDrop(false)}
            >
              انصراف
            </button>
          </>
        ) : (
          <>
            <Link className='btn btn-sm btn-primary' to={ticketPath}>
              <i className='bi bi-reply' aria-hidden='true' /> رفتن به تیکت
            </Link>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => setConfirmingDrop(true)}
            >
              حذف
            </button>
          </>
        )}
      </div>
    )

  return (
    <div
      className={`mp-screen-share-bar mp-screen-record-bar is-${state.status}`}
      role='status'
      aria-labelledby={titleId}
      dir='rtl'
    >
      <span className='mp-screen-share-bar__dot' aria-hidden='true' />
      <span className='mp-screen-share-bar__icon' aria-hidden='true'>
        <i className='bi bi-camera-video' />
      </span>
      <span className='mp-screen-share-bar__words'>
        <strong id={titleId}>{paused ? 'ضبط صفحه در مکث است' : 'در حال ضبط صفحه'}</strong>
        <small>
          {clockLabel(seconds)} از {clockLabel(SCREEN_MAX_SECONDS)}؛ با «پایان ضبط» به تیکت
          برمی‌گردید و ویدئو به پاسخ شما پیوست می‌شود.
        </small>
      </span>
      <button
        type='button'
        className='btn btn-sm btn-light'
        onClick={paused ? resumeScreenRecording : pauseScreenRecording}
      >
        <i className={`bi ${paused ? 'bi-record-fill' : 'bi-pause-fill'}`} aria-hidden='true' />
        {paused ? 'ادامه' : 'مکث'}
      </button>
      <button
        type='button'
        className='btn btn-sm btn-danger'
        onClick={() => {
          stopScreenRecording()
          navigate(ticketPath)
        }}
      >
        <i className='bi bi-stop-fill' aria-hidden='true' /> پایان ضبط
      </button>
      <button
        type='button'
        className='btn btn-sm btn-icon btn-light'
        onClick={() => stopScreenRecording(true)}
        aria-label='انصراف از ضبط صفحه؛ چیزی نگه داشته نمی‌شود'
        title='انصراف از ضبط'
      >
        <i className='bi bi-x-lg' aria-hidden='true' />
      </button>
    </div>
  )
}
